import React, { useState, useEffect, useRef } from "react";
import ReactDOM from "react-dom";
import "./WorkspaceDashboard.css";
import "./OnboardingHover.css";
import { useNavigate } from "react-router-dom";
import OnboardingModal from "./OnboardingModal";

const WorkspaceDashboard = () => {
	const [workspaces, setWorkspaces] = useState([]);
	const [loading, setLoading] = useState(true);
	const [error, setError] = useState(null);
	const [lastUpdatedAt, setLastUpdatedAt] = useState(null);
	const [refreshFailed, setRefreshFailed] = useState(false);
	const [searchTerm, setSearchTerm] = useState("");
	const [sortConfig, setSortConfig] = useState({
		key: "healthScore",
		direction: "desc",
	});
	const [onboardingModal, setOnboardingModal] = useState({
		isVisible: false,
		workspaceId: null,
		workspaceName: ""
	});
	const [scoreTooltip, setScoreTooltip] = useState(null);
	const [billingOptedOutIds, setBillingOptedOutIds] = useState(new Set());
	const scoreTooltipRef = useRef(null);
	const navigate = useNavigate();

	useEffect(() => {
		fetchWorkspaceData();
		fetchBillingOptOuts();
		// Poll every 60s so online user counts stay fresh without a manual page reload.
		// Backend caches the Redis lookup for 30s, so worst-case lag is 90s (30s cache + 60s poll).
		const interval = setInterval(fetchWorkspaceData, 60000);
		return () => clearInterval(interval); // cleanup on unmount
	}, []);

	// Dismiss score tooltip on scroll
	useEffect(() => {
		if (!scoreTooltip) return;
		const dismiss = () => setScoreTooltip(null);
		window.addEventListener("scroll", dismiss, true);
		return () => window.removeEventListener("scroll", dismiss, true);
	}, [scoreTooltip]);

	const fetchWorkspaceData = async () => {
		try {
			const response = await fetch(
				`${process.env.REACT_APP_BASE_URL}/workspaces/stats`,
			);
			if (!response.ok) {
				throw new Error("Failed to fetch workspace data");
			}
			const data = await response.json();

			// Render the main dashboard immediately from /stats. Transcript metrics
			// live behind a separate, heavier (secondary-read) endpoint, so we do
			// NOT await them here — we carry forward any previously-known transcript
			// values (avoids a flicker to 0 on each poll) and let fetchTranscriptMetrics
			// merge fresh values in asynchronously below.
			setWorkspaces((prev) => {
				const prevMap = {};
				(prev || []).forEach((w) => {
					prevMap[w.workspaceId] = w;
				});
				return (Array.isArray(data) ? data : []).map((ws) => {
					const p = prevMap[ws.workspaceId] || {};
					return {
						...ws,
						totalTranscripts: p.totalTranscripts ?? 0,
						avgTranscriptDuration: p.avgTranscriptDuration ?? 0,
						avgTranscriptWords: p.avgTranscriptWords ?? 0,
						avgTranscriptChars: p.avgTranscriptChars ?? 0,
						transcriptPercentageChange: p.transcriptPercentageChange ?? 0,
						totalCopilotResponses: p.totalCopilotResponses ?? 0,
					};
				});
			});
			setLastUpdatedAt(new Date());
			setRefreshFailed(false);
			setError(null);
			setLoading(false);

			// Fire-and-forget — does not block the render above.
			fetchTranscriptMetrics();
		} catch (err) {
			// Keep showing the previous workspaces — a single failed poll
			// shouldn't blank the page. Only surface a fatal error if we have
			// nothing to display yet (initial load failure).
			setRefreshFailed(true);
			setLoading(false);
			setWorkspaces((prev) => {
				if (!prev || prev.length === 0) setError(err.message);
				return prev;
			});
		}
	};
	// Fetch transcript metrics independently and merge them onto the already-
	// rendered workspaces. A slow or failed transcript query therefore never
	// delays the main dashboard.
	const fetchTranscriptMetrics = async () => {
		try {
			const res = await fetch(
				`${process.env.REACT_APP_BASE_URL}/workspaces/transcript-metrics`,
			);
			if (!res.ok) return;
			const tData = await res.json();
			const map = {};
			(Array.isArray(tData) ? tData : []).forEach((t) => {
				map[t.workspaceId] = t;
			});
			setWorkspaces((prev) =>
				(prev || []).map((ws) => {
					const t = map[ws.workspaceId];
					if (!t) return ws;
					return {
						...ws,
						totalTranscripts: t.totalTranscripts || 0,
						avgTranscriptDuration: t.avgTranscriptDuration || 0,
						avgTranscriptWords: t.avgTranscriptWords || 0,
						avgTranscriptChars: t.avgTranscriptChars || 0,
						transcriptPercentageChange: t.transcriptPercentageChange || 0,
						totalCopilotResponses: t.totalCopilotResponses || 0,
					};
				}),
			);
		} catch (err) {
			console.error("Error fetching transcript metrics:", err);
		}
	};
	const fetchBillingOptOuts = async () => {
		try {
			const response = await fetch(
				`${process.env.REACT_APP_BASE_URL}/workspaces/billing-optouts`,
			);
			if (!response.ok) return;
			const data = await response.json();
			setBillingOptedOutIds(new Set(data.optedOutIds || []));
		} catch (err) {
			console.error("Error fetching billing opt-outs:", err);
		}
	};
	const handleDeleteWorkspace = async (e, workspaceId) => {
		e.stopPropagation();

		if (
			window.confirm(
				"Are you sure you want to delete this workspace? This action cannot be undone.",
			)
		) {
			try {
				console.log(`Attempting to delete workspace ${workspaceId}`);

				// Show a loading indicator
				const deleteButton = e.target;
				const originalText = deleteButton.innerText;
				deleteButton.innerText = "Deleting...";
				deleteButton.disabled = true;

				// Use the correct URL
				const response = await fetch(
					`${process.env.REACT_APP_BASE_URL}/workspaces/delete-workspace`,
					{
						method: "POST",
						headers: {
							"Content-Type": "application/json",
						},
						body: JSON.stringify({
							workspaceId: workspaceId,
						}),
					},
				);

				// Restore button state
				deleteButton.innerText = originalText;
				deleteButton.disabled = false;

				const data = await response.json();
				console.log(`Delete response:`, data);

				if (!response.ok || !data.success) {
					throw new Error(
						data.error ||
							data.message ||
							"Failed to delete workspace",
					);
				}

				// Update local state
				setWorkspaces(
					workspaces.filter((w) => w.workspaceId !== workspaceId),
				);

				alert(
					"Workspace deleted successfully! The page will refresh to update the workspace list.",
				);

				// Force a page refresh after a short delay
				setTimeout(() => {
					window.location.reload();
				}, 1500);
			} catch (error) {
				console.error("Error deleting workspace:", error);
				alert(`Failed to delete workspace: ${error.message}`);
			}
		}
	};
	// Helper function to format numbers with commas
	const formatNumber = (num) => {
		return new Intl.NumberFormat().format(num);
	};
	const formatPercentageChange = (value) => {
		if (value === undefined || value === null) return "N/A";
		const formatted = value.toFixed(1);
		// Add a plus sign for positive values, negative values already have their sign
		return `${value > 0 ? "+" : ""}${formatted}%`;
	};

	// Unified health score (0-100) driving both badge and sort
	const getHealthScore = (workspace) => {
		const activeMembers = workspace.totalActiveMembers || 0;
		if (activeMembers === 0) {
			return { score: 0, label: "Growing", class: "growing", maxEng: 40, maxComm: 25, dimensions: { engagement: 0, communication: 0, adoption: 0, growth: 0 } };
		}

		const clamp = (val, min, max) => Math.min(Math.max(val, min), max);

		// Engagement (40%): interactions per member, 30+ = full marks
		const engagement = clamp((workspace.totalInteractions || 0) / activeMembers / 30, 0, 1) * 100;

		// Communication (25%): DMs per member, 20+ = full marks
		const communication = clamp((workspace.totalDMMessages || 0) / activeMembers / 20, 0, 1) * 100;

		// Adoption (20%): onboarding percentage (already 0-100)
		const adoption = clamp(workspace.onboardingPercentage || 0, 0, 100);

		// Growth (15%): average of trend percentages, normalized
		const avgTrend = (
			(workspace.dmPercentageChange || 0) +
			(workspace.knockPercentageChange || 0) +
			(workspace.invitePercentageChange || 0)
		) / 3;
		const growth = clamp((avgTrend + 10) / 20 * 100, 0, 100);

		const score = Math.round(
			engagement * 0.40 +
			communication * 0.25 +
			adoption * 0.20 +
			growth * 0.15
		);

		let label, cls;
		if (score >= 75) { label = "Excellent"; cls = "excellent"; }
		else if (score >= 50) { label = "Good"; cls = "good"; }
		else if (score >= 25) { label = "Moderate"; cls = "moderate"; }
		else { label = "Growing"; cls = "growing"; }

		return {
			score, label, class: cls,
			maxEng: 40, maxComm: 25,
			dimensions: {
				engagement: Math.round(engagement * 0.40),
				communication: Math.round(communication * 0.25),
				adoption: Math.round(adoption * 0.20),
				growth: Math.round(growth * 0.15),
			}
		};
	};

	// Helper function to format date
	const formatDate = (dateString) => {
		const date = new Date(dateString);
		return date.toLocaleDateString("en-US", {
			year: "numeric",
			month: "short",
			day: "numeric",
		});
	};

	// Helper function to get trial status and class
	const getTrialStatus = (workspace) => {
		if (!workspace.createdAt) return { label: "Paid", class: "trial-paid" };

		const createdDate = new Date(workspace.createdAt);
		const now = new Date();
		const diffMs = now - createdDate;
		const daysSinceCreation = Math.floor(diffMs / (1000 * 60 * 60 * 24));
		const trialDays = 15;

		if (daysSinceCreation < trialDays) {
			const daysLeft = trialDays - daysSinceCreation;
			if (daysLeft <= 5) {
				return { label: `Free Trial · ${daysLeft}d left`, class: "trial-warning" };
			}
			return { label: `Free Trial · ${daysLeft}d left`, class: "trial-active" };
		}
		return { label: "Paid", class: "trial-paid" };
	};

	// Onboarding percentage is now calculated on the backend based on:
	// - Profile picture uploaded (profileImage not null)
	// - Desktop app installed (isDesktopAppInstalled = 'YES')
	// - Mobile app installed (isMobileAppInstalled = 'YES')
	// Formula: (completed parameters / total parameters) * 100
	// where total parameters = number of users * 3

	// Helper function to get progress circle class based on percentage
	const getProgressClass = (percentage) => {
		if (percentage >= 85) return "excellent";
		if (percentage >= 70) return "good";
		if (percentage >= 50) return "moderate";
		return "poor";
	};

	// Helper function to format average values
	const formatAverage = (value) => {
		if (value === undefined || value === null) return "0";
		return parseFloat(value).toFixed(1);
	};

	// Helper function to calculate averages based on active members
	const calculateAverages = (workspace) => {
		const activeMembers = workspace.totalActiveMembers || 0;

		if (activeMembers === 0) {
			return {
				avgDropIn: 0,
				avgDMs: 0,
			};
		}

		// Average Drop In = Total Interactions / Active Members
		const avgDropIn =
			Math.round(
				((workspace.totalInteractions || 0) / activeMembers) * 100,
			) / 100;

		// Average DMs = Total DM Messages / Active Members
		const avgDMs =
			Math.round(
				((workspace.totalDMMessages || 0) / activeMembers) * 100,
			) / 100;

		return {
			avgDropIn,
			avgDMs,
		};
	};

	const handleWorkspaceClick = (workspace) => {
		if (workspace.workspaceId) {
			navigate(
				`/workspace/${workspace.workspaceId}/${encodeURIComponent(workspace.name)}/users`,
			);
		} else {
			console.error("Workspace ID is missing:", workspace);
		}
	};

	const handleOnboardingHover = (workspace) => {
		setOnboardingModal({
			isVisible: true,
			workspaceId: workspace.workspaceId,
			workspaceName: workspace.name
		});
	};

	const handleModalClose = () => {
		setOnboardingModal({
			isVisible: false,
			workspaceId: null,
			workspaceName: ""
		});
	};

	// Sorting function
	const handleSort = (key) => {
		let direction = "desc";
		if (sortConfig.key === key && sortConfig.direction === "desc") {
			direction = "asc";
		}
		setSortConfig({ key, direction });
	};

	// Filter workspaces based on search term
	const getFilteredWorkspaces = () => {
		if (!searchTerm.trim()) return workspaces;
		
		return workspaces.filter(workspace => 
			workspace.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
			(workspace.description && workspace.description.toLowerCase().includes(searchTerm.toLowerCase()))
		);
	};

	// Get sorted and filtered data
	const getSortedWorkspaces = () => {
		const filteredWorkspaces = getFilteredWorkspaces();
		
		if (!sortConfig.key) return filteredWorkspaces;

		// Special case for "Active Now" - filter to show only workspaces with active users
		if (sortConfig.key === "activeNow") {
			return filteredWorkspaces.filter(workspace => workspace.activeUsers > 0);
		}

		// Billable - workspaces included in revenue (not opted out of billing)
		if (sortConfig.key === "billable") {
			return filteredWorkspaces.filter(workspace => !billingOptedOutIds.has(workspace.workspaceId));
		}

		// Non-Billable - workspaces opted out of billing
		if (sortConfig.key === "nonBillable") {
			return filteredWorkspaces.filter(workspace => billingOptedOutIds.has(workspace.workspaceId));
		}

		return [...filteredWorkspaces].sort((a, b) => {
			let aValue = a[sortConfig.key];
			let bValue = b[sortConfig.key];

			// Handle different data types
			if (sortConfig.key === "healthScore") {
				aValue = getHealthScore(a).score;
				bValue = getHealthScore(b).score;
			} else if (sortConfig.key === "createdAt") {
				aValue = new Date(aValue);
				bValue = new Date(bValue);
			} else if (
				typeof aValue === "string" &&
				sortConfig.key === "name"
			) {
				aValue = aValue.toLowerCase();
				bValue = bValue.toLowerCase();
			} else if (typeof aValue === "number" || !isNaN(aValue)) {
				aValue = Number(aValue);
				bValue = Number(bValue);
			}

			if (aValue < bValue) {
				return sortConfig.direction === "asc" ? -1 : 1;
			}
			if (aValue > bValue) {
				return sortConfig.direction === "asc" ? 1 : -1;
			}
			return 0;
		});
	};

	if (loading)
		return (
			<div className="loading">
				<div className="loader-container">
					<div className="loader-ring"></div>
					<div className="loader-ring"></div>
					<div className="loader-ring"></div>
					<div className="loader-dots">
						<div className="loader-dot"></div>
						<div className="loader-dot"></div>
						<div className="loader-dot"></div>
					</div>
				</div>
			</div>
		);
	// Only treat error as fatal when there is nothing to show. Once we have
	// workspaces in hand, a transient failure becomes an inline "stale" banner.
	if (error && (!workspaces || workspaces.length === 0))
		return <div className="error">Error: {error}</div>;

	const formatLastUpdated = (d) => {
		if (!d) return "";
		return d.toLocaleTimeString([], { hour: "2-digit", minute: "2-digit", second: "2-digit" });
	};

	// Calculate aggregated statistics
	const getAggregatedStats = () => {
		if (!workspaces.length) return {};

		const stats = workspaces.reduce(
			(acc, workspace) => {
				acc.totalWorkspaces += 1;
				acc.totalActiveMembers += workspace.totalActiveMembers || 0;
				acc.totalDMMessages += workspace.totalDMMessages || 0;
				acc.totalStackMessages += workspace.totalStackMessages || 0;
				acc.totalInteractions += workspace.totalInteractions || 0;
				return acc;
			},
			{
				totalWorkspaces: 0,
				totalActiveMembers: 0,
				totalDMMessages: 0,
				totalStackMessages: 0,
				totalInteractions: 0,
			},
		);

		// Calculate total messages
		stats.totalMessages = stats.totalDMMessages + stats.totalStackMessages;

		return stats;
	};

	const stats = getAggregatedStats();

	return (
		<div className="dashboard-container">
			{/* Header Section */}
			<div className="dashboard-header">
				<h1>All Workspaces</h1>
				{refreshFailed && lastUpdatedAt && (
					<div
						style={{
							marginTop: 8,
							padding: "6px 12px",
							background: "#3a2a18",
							color: "#f5b773",
							border: "1px solid #6b4a25",
							borderRadius: 6,
							fontSize: 13,
							display: "inline-block",
						}}
					>
						⚠ Couldn’t refresh — showing data as of {formatLastUpdated(lastUpdatedAt)}.
						Will retry on next poll.
					</div>
				)}
			</div>

			{/* Key Metrics Grid */}
			<div className="metrics-grid">
				{/* Live Activity Card */}
				{(() => {
					const liveWorkspaces = workspaces.filter(w => w.activeUsers > 0);
					const liveWorkspaceCount = liveWorkspaces.length;
					const totalOnlineUsers = liveWorkspaces.reduce((sum, w) => sum + (w.activeUsers || 0), 0);
					return (
						<div className="metric-card live">
							<div className="metric-value">
								{formatNumber(totalOnlineUsers)}
							</div>
							<div className="metric-label">{totalOnlineUsers === 1 ? "User" : "Users"} Online</div>
							<div className="metric-sub">across {liveWorkspaceCount} {liveWorkspaceCount === 1 ? "workspace" : "workspaces"}</div>
						</div>
					);
				})()}
				<div className="metric-card primary">
					<div className="metric-value">
						{formatNumber(stats.totalWorkspaces)}
					</div>
					<div className="metric-label">Total Workspaces</div>
				</div>
				<div className="metric-card success">
					<div className="metric-value">
						{formatNumber(stats.totalActiveMembers)}
					</div>
					<div className="metric-label">Activated Members</div>
				</div>
				<div className="metric-card info">
					<div className="metric-value">
						{formatNumber(stats.totalMessages)}
					</div>
					<div className="metric-label">Total Messages</div>
				</div>
				<div className="metric-card warning">
					<div className="metric-value">
						{formatNumber(stats.totalInteractions)}
					</div>
					<div className="metric-label">Total Interactions</div>
				</div>
			</div>

			{/* Sort Controls */}
			<div className="sort-controls">
				<div className="sort-buttons">
					<button
						className={
							sortConfig.key === "activeNow" ? "active" : ""
						}
						onClick={() => handleSort("activeNow")}
					>
						Active Now
					</button>
					<button
						className={
							sortConfig.key === "healthScore"
								? "active"
								: ""
						}
						onClick={() => handleSort("healthScore")}
					>
						Most Active
					</button>
					<button
						className={
							sortConfig.key === "totalActiveMembers"
								? "active"
								: ""
						}
						onClick={() => handleSort("totalActiveMembers")}
					>
						Most Members
					</button>
					<button
						className={
							sortConfig.key === "createdAt" ? "active" : ""
						}
						onClick={() => handleSort("createdAt")}
					>
						Recently Created
					</button>
					<button
						className={sortConfig.key === "name" ? "active" : ""}
						onClick={() => handleSort("name")}
					>
						Alphabetical
					</button>
					<button
						className={sortConfig.key === "billable" ? "active" : ""}
						onClick={() => handleSort("billable")}
					>
						Billable
					</button>
					<button
						className={sortConfig.key === "nonBillable" ? "active" : ""}
						onClick={() => handleSort("nonBillable")}
					>
						Non-Billable
					</button>
				</div>
				
				{/* Search Input */}
				<div className="search-container">
					<div className="search-input-wrapper">
						<svg className="search-icon" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
							<circle cx="11" cy="11" r="8"></circle>
							<path d="m21 21-4.35-4.35"></path>
						</svg>
						<input
							type="text"
							placeholder="Search workspaces..."
							value={searchTerm}
							onChange={(e) => setSearchTerm(e.target.value)}
							className="search-input"
						/>
						{searchTerm && (
							<button
								onClick={() => setSearchTerm("")}
								className="clear-search-btn"
								title="Clear search"
							>
								<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
									<line x1="18" y1="6" x2="6" y2="18"></line>
									<line x1="6" y1="6" x2="18" y2="18"></line>
								</svg>
							</button>
						)}
					</div>
				</div>
			</div>

			{/* Search Results Info */}
			{searchTerm && (
				<div className="search-results-info">
					{getSortedWorkspaces().length === 0 ? (
						<span>No workspaces found matching "{searchTerm}"</span>
					) : getSortedWorkspaces().length === 1 ? (
						<span>Found 1 workspace matching "{searchTerm}"</span>
					) : (
						<span>Found {getSortedWorkspaces().length} workspaces matching "{searchTerm}"</span>
					)}
				</div>
			)}

			{/* Workspace Cards Grid */}
			<div className="workspaces-grid">
				{getSortedWorkspaces().map((workspace, index) => {
					const strength = getHealthScore(workspace);
					const totalMembers = workspace.totalActiveMembers;
					const trialStatus = getTrialStatus(workspace);
					const onboardingPercentage = workspace.onboardingPercentage || 0;
					const progressClass =
						getProgressClass(onboardingPercentage);
					const averages = calculateAverages(workspace);

					return (
						<div
							key={index}
							className="workspace-item clickable-workspace"
							onClick={() => handleWorkspaceClick(workspace)}
						>
							{/* Card Header */}
							<div className="wcard-header">
								<div className="wcard-title-row">
									<div className="workspace-rank">{index + 1}</div>
									<div className="wcard-name">
										{workspace.name}
										{!workspace.workspaceId && (
											<span className="error-text"> (ID missing)</span>
										)}
									</div>
								<div className="strength-chips">
									<span className={`strength-label-chip ${strength.class}`}>{strength.label}</span>
									<span
										className={`strength-score-chip ${strength.score > 60 ? 'high' : strength.score >= 20 ? 'medium' : strength.score > 0 ? 'low' : 'inactive'}`}
										onMouseEnter={(e) => {
											e.stopPropagation();
											const rect = e.currentTarget.getBoundingClientRect();
											setScoreTooltip({ id: workspace.workspaceId, rect, strength });
										}}
										onMouseLeave={(e) => {
											e.stopPropagation();
											setScoreTooltip(null);
										}}
									>
										{strength.score}
									</span>
									<span className={`trial-status-chip ${trialStatus.class}`}>{trialStatus.label}</span>
									{billingOptedOutIds.has(workspace.workspaceId) && (
										<span className="billing-excluded-chip" title="Excluded from revenue calculation">
											No billing
										</span>
									)}
								</div>
								</div>
								<div className="wcard-header-right">
									<div className="wcard-online">
										{workspace.activeUsers > 0 && <span className="wcard-online-dot"></span>}
										<span className={`wcard-online-value ${workspace.activeUsers === 0 ? "inactive" : ""}`}>{formatNumber(workspace.activeUsers)}</span>
										<span className="wcard-online-sep">/</span>
										<span className="wcard-online-total">{formatNumber(totalMembers)}</span>
									</div>
									<div className="wcard-date">Created {formatDate(workspace.createdAt)}</div>
								</div>
							</div>

							{/* Stats Grid */}
							<div
								className="wcard-stats"
								onMouseMove={(e) => {
									const rect = e.currentTarget.getBoundingClientRect();
									e.currentTarget.style.setProperty("--mx", `${e.clientX - rect.left}px`);
									e.currentTarget.style.setProperty("--my", `${e.clientY - rect.top}px`);
								}}
								onMouseLeave={(e) => {
									e.currentTarget.style.removeProperty("--mx");
									e.currentTarget.style.removeProperty("--my");
								}}
							>
								<div className={`wcard-stat scored ${strength.dimensions.engagement > strength.maxEng * 0.6 ? 'sc-high' : strength.dimensions.engagement >= strength.maxEng * 0.3 ? 'sc-medium' : strength.dimensions.engagement > 0 ? 'sc-low' : ''}`}>
									<span className="wcard-stat-val">{formatNumber(workspace.totalInteractions)}</span>
									<span className="wcard-stat-lbl">Interactions</span>
								</div>
								<div className="wcard-stat">
									<span className="wcard-stat-val">{formatNumber(workspace.totalDMMessages)}</span>
									<span className="wcard-stat-lbl">DMs</span>
								</div>
								<div className="wcard-stat">
									<span className="wcard-stat-val">{formatNumber(workspace.totalStackMessages)}</span>
									<span className="wcard-stat-lbl">Stack Msgs</span>
								</div>
								<div className={`wcard-stat scored ${strength.dimensions.communication > strength.maxComm * 0.6 ? 'sc-high' : strength.dimensions.communication >= strength.maxComm * 0.3 ? 'sc-medium' : strength.dimensions.communication > 0 ? 'sc-low' : ''}`}>
									<span className="wcard-stat-val">{formatAverage(averages.avgDMs)}</span>
									<span className="wcard-stat-lbl">Avg DMs</span>
								</div>
								<div className={`wcard-stat scored ${strength.dimensions.engagement > strength.maxEng * 0.6 ? 'sc-high' : strength.dimensions.engagement >= strength.maxEng * 0.3 ? 'sc-medium' : strength.dimensions.engagement > 0 ? 'sc-low' : ''}`}>
									<span className="wcard-stat-val">{formatAverage(averages.avgDropIn)}</span>
									<span className="wcard-stat-lbl">Avg Drop-ins</span>
								</div>
								<div className="wcard-stat">
									<span className="wcard-stat-val">{formatNumber(workspace.totalInvitedMembers)}</span>
									<span className="wcard-stat-lbl">Invited</span>
								</div>
								<div className="wcard-stat">
									<span className="wcard-stat-val">{formatNumber(workspace.totalTranscripts)}</span>
									<span className="wcard-stat-lbl">Transcripts</span>
								</div>
								<div
									className="wcard-stat"
									title={`Avg ${formatNumber(workspace.avgTranscriptWords)} words per transcript`}
								>
									<span className="wcard-stat-val">{formatAverage(workspace.avgTranscriptDuration)}m</span>
									<span className="wcard-stat-lbl">Avg Length</span>
								</div>
								<div
									className="wcard-stat"
									title="Genuine copilot responses (excludes auto-summaries)"
								>
									<span className="wcard-stat-val">{formatNumber(workspace.totalCopilotResponses)}</span>
									<span className="wcard-stat-lbl">Copilot</span>
								</div>
							</div>

							{/* Footer: Trends + Onboarding + Actions */}
							<div className="wcard-footer">
								<div className="wcard-trends">
									<span className={`wcard-trend ${workspace.dmPercentageChange > 0 ? "up" : workspace.dmPercentageChange < 0 ? "down" : ""}`}>
										DMs {formatPercentageChange(workspace.dmPercentageChange)}
									</span>
									<span className={`wcard-trend ${workspace.knockPercentageChange > 0 ? "up" : workspace.knockPercentageChange < 0 ? "down" : ""}`}>
										Knocks {formatPercentageChange(workspace.knockPercentageChange)}
									</span>
									<span className={`wcard-trend ${workspace.invitePercentageChange > 0 ? "up" : workspace.invitePercentageChange < 0 ? "down" : ""}`}>
										Invites {formatPercentageChange(workspace.invitePercentageChange)}
									</span>
									<span className={`wcard-trend ${workspace.transcriptPercentageChange > 0 ? "up" : workspace.transcriptPercentageChange < 0 ? "down" : ""}`}>
										Transcripts {formatPercentageChange(workspace.transcriptPercentageChange)}
									</span>
								</div>
								<div className="wcard-footer-right">
									<div
										className="wcard-onboarding hoverable-onboarding"
										onClick={(e) => {
											e.stopPropagation();
											handleOnboardingHover(workspace);
										}}
									>
										<div className="progress-circle">
											<svg className="progress-ring" width="44" height="44">
												<circle
													className="progress-ring-background"
													stroke="rgba(255,255,255,0.06)"
													strokeWidth="3"
													fill="transparent"
													r="18"
													cx="22"
													cy="22"
												/>
												<circle
													className={`progress-ring-progress ${progressClass}`}
													strokeWidth="3"
													fill="transparent"
													r="18"
													cx="22"
													cy="22"
													strokeDasharray={`${2 * Math.PI * 18}`}
													strokeDashoffset={`${2 * Math.PI * 18 * (1 - onboardingPercentage / 100)}`}
												/>
											</svg>
											<div className="progress-text">
												<div className="progress-percentage wcard-ob-pct">{onboardingPercentage}%</div>
											</div>
										</div>
									</div>
								</div>
							</div>
						</div>
					);
				})}
			</div>
			
			{/* Onboarding Details Modal */}
			<OnboardingModal
				isVisible={onboardingModal.isVisible}
				workspaceId={onboardingModal.workspaceId}
				workspaceName={onboardingModal.workspaceName}
				onClose={handleModalClose}
			/>

			{/* Score Tooltip Portal */}
			{scoreTooltip && ReactDOM.createPortal(
				<div
					className="score-tooltip"
					ref={scoreTooltipRef}
					style={{
						top: scoreTooltip.rect.bottom + 8,
						left: Math.max(8, Math.min(scoreTooltip.rect.right - 280, window.innerWidth - 288)),
					}}
					onMouseEnter={() => {}}
					onMouseLeave={() => setScoreTooltip(null)}
				>
					<div className="score-tooltip-header">
						<span className="score-tooltip-title">Health Score Breakdown</span>
						<span className={`score-tooltip-total ${scoreTooltip.strength.score > 60 ? 'high' : scoreTooltip.strength.score >= 20 ? 'medium' : scoreTooltip.strength.score > 0 ? 'low' : 'inactive'}`}>{scoreTooltip.strength.score}/100</span>
					</div>
					{[
						{ key: "engagement", label: "Engagement", max: 40, desc: "Total knocks, invites & kicks divided by members. Hits max at 30+ per member.", value: scoreTooltip.strength.dimensions.engagement },
						{ key: "communication", label: "Communication", max: 25, desc: "Total DMs sent divided by members. Hits max at 20+ DMs per member.", value: scoreTooltip.strength.dimensions.communication },
						{ key: "adoption", label: "Adoption", max: 20, desc: "% of members who have set up their profile, desktop app & mobile app.", value: scoreTooltip.strength.dimensions.adoption },
						{ key: "growth", label: "Growth", max: 15, desc: "Average month-over-month change in DMs, knocks & invites. +10% or better = max.", value: scoreTooltip.strength.dimensions.growth },
					].map((dim) => (
						<div className="score-dim" key={dim.key}>
							<div className="score-dim-top">
								<span className="score-dim-label">{dim.label} <span className="score-dim-meta">{dim.max}%</span></span>
								<span className={`score-dim-value ${dim.value > dim.max * 0.6 ? 'high' : dim.value >= dim.max * 0.3 ? 'medium' : dim.value > 0 ? 'low' : 'inactive'}`}>{dim.value}</span>
							</div>
							<div className="score-dim-bar-bg">
								<div
									className={`score-dim-bar-fill ${dim.value > dim.max * 0.6 ? 'high' : dim.value >= dim.max * 0.3 ? 'medium' : dim.value > 0 ? 'low' : 'inactive'}`}
									style={{ width: `${(dim.value / dim.max) * 100}%` }}
								></div>
							</div>
							<div className="score-dim-desc">{dim.desc}</div>
						</div>
					))}
				</div>,
				document.body
			)}
		</div>
	);
};

export default WorkspaceDashboard;
